/* eslint-disable prefer-object-spread, no-param-reassign */
import axios from 'axios';
import { DateTime } from 'luxon';
import he from 'he';

class BrixModuleEventCalendar {
  static init(...parameters) {
    return new BrixModuleEventCalendar(...parameters);
  }

  constructor(selector) {
    this.selector = selector;

    this.data = null;
    this.displayData = null;
    this.remainingData = null;
    this.taxonomyTermsFilter = {};

    this.url = new URL(window.location);
    this.searchParamName = 'filter';
    this.searchParamSeparator = ' ';

    this.cols = null;
    this.loadedMonths = 0;

    this.calendarWrapper = this.selector.querySelector('.calendar');
    this.taxonomyTerms = this.selector.querySelectorAll('.filter .taxonomy-input');
    this.showAllTerms = this.selector.querySelectorAll('.filter .show-all-terms');
    this.loadMoreButton = this.selector.querySelector('.show-more');

    this.currentLang = document.documentElement.lang;
    this.apiEndpoint = this.selector.querySelector('.api-url').value;

    axios.get(this.apiEndpoint)
      .then((response) => {
        this.data = response.data;
        this.loadData();
      });

    this.setupListeners();
    this.checkSearchParam();
  }

  setupListeners() {
    Array.from(this.taxonomyTerms).forEach((taxonomyTerm) => {
      taxonomyTerm.addEventListener('change', () => this.filterCalendar());
    });

    // NOTE: At the moment one 'show all'-input is created for each taxonomy.
    // It might be relevant in the future to have just one input for all.
    Array.from(this.showAllTerms).forEach((input) => {
      input.addEventListener('change', (e) => this.showAllTaxonomyTerms(e));
    });

    this.loadMoreButton.addEventListener('click', () => {
      this.loadData();
    });
  }

  loadData() {
    if (this.loadedMonths < this.data.length) {
      this.loadedMonths += 3; // Load three months at a time
      this.displayData = this.data.slice(0, this.loadedMonths);
      this.remainingData = this.data.slice(this.loadedMonths);
      this.filterCalendar();
    }
  }

  checkSearchParam() {
    const currentValue = this.url.searchParams.get(this.searchParamName);
    if (!currentValue || !this.taxonomyTerms) return;

    const paramItems = currentValue.split(this.searchParamSeparator) || [];

    this.taxonomyTerms.forEach((taxonomyTerm) => {
      if (paramItems.includes(taxonomyTerm.dataset.slug)) {
        taxonomyTerm.checked = true;
      }
    });
  }

  showAllTaxonomyTerms(event) {
    this.taxonomyTerms.forEach((taxonomyTerm) => {
      if (event.target.checked && event.target.dataset.taxonomy === taxonomyTerm.dataset.taxonomy) {
        taxonomyTerm.checked = false;
      }
    });
    this.filterCalendar();
  }

  handleSearchParamItem(item, removeItem = false) {
    const currentValue = this.url.searchParams.get(this.searchParamName);
    const items = (currentValue && currentValue.split(this.searchParamSeparator)) || [];

    if (!removeItem && !items.includes(item)) {
      items.push(item);
    } else if (removeItem && items.includes(item)) {
      items.splice(items.indexOf(item), 1);
    } else return;

    if (items.length) {
      const newValue = items.join(this.searchParamSeparator);
      this.url.searchParams.set(this.searchParamName, newValue);
    } else {
      this.url.searchParams.delete(this.searchParamName);
    }
    window.history.pushState({}, '', this.url);
  }

  createFilter() {
    Array.from(this.taxonomyTerms).forEach((taxonomyTerm) => {
      const { taxonomy, termId, slug } = taxonomyTerm.dataset;
      if (taxonomyTerm.checked) {
        this.handleSearchParamItem(slug);
        // if (taxonomyTerm.checked && !isIncluded) {
        if (!this.taxonomyTermsFilter[taxonomy]) {
          this.taxonomyTermsFilter[taxonomy] = [];
        }

        if (!this.taxonomyTermsFilter[taxonomy].includes(termId)) {
          this.taxonomyTermsFilter[taxonomy].push(termId);
        }
      }

      if (!taxonomyTerm.checked) {
        this.handleSearchParamItem(slug, true);
        if (
          this.taxonomyTermsFilter[taxonomy] && this.taxonomyTermsFilter[taxonomy].includes(termId)
        ) {
          const index = this.taxonomyTermsFilter[taxonomy].indexOf(termId);
          this.taxonomyTermsFilter[taxonomy].splice(index, 1);

          if (this.taxonomyTermsFilter[taxonomy].length === 0) {
            delete this.taxonomyTermsFilter[taxonomy];
          }
        }
      }
    });

    Array.from(this.showAllTerms).forEach((input) => {
      const isTaxonomyFiltered = Array.from(this.taxonomyTerms).find(
        (term) => input.dataset.taxonomy === term.dataset.taxonomy && term.checked,
      );
      input.checked = !isTaxonomyFiltered;
    });
  }

  filterOccasions(occasions, filter) {
    const filterKeys = Object.keys(filter);
    const filteredOccasions = occasions.filter((occasion) => {
      occasion.taxonomyTerms.forEach((taxonomyTerm) => {
        if (!occasion[taxonomyTerm.taxonomy]) {
          occasion[taxonomyTerm.taxonomy] = [`${taxonomyTerm.taxonomy}-${taxonomyTerm.term_id}`];
        } else {
          occasion[taxonomyTerm.taxonomy].push(`${taxonomyTerm.taxonomy}-${taxonomyTerm.term_id}`);
        }
      });

      return filterKeys.every((key) => {
        // if (!filter[key].length) return true; // NOTE: Can we remove this check?
        if (!occasion[key]) { return false; }
        return filter[key].find((filterTerm) => occasion[key].includes(filterTerm));
      });
    });

    return filteredOccasions;
  }

  filterCalendar() {
    let filteredData = this.displayData;
    let showLoadMoreButton = false;

    // Create filter from checked terms and saves it to this.taxonomyTermsFilter
    this.createFilter();

    if (this.remainingData.length > 0) {
      showLoadMoreButton = true;
    }

    filteredData = this.displayData.map((month) => {
      month = Object.assign({}, month); // { ...month };
      const filteredOccasions = this.filterOccasions(month.occasions, this.taxonomyTermsFilter);
      month.occasions = filteredOccasions;

      return month;
    });

    this.renderCalendar(filteredData);
    this.toggleButton(this.loadMoreButton, showLoadMoreButton);
  }

  renderCalendar(data) {
    // Removes loader...
    while (this.calendarWrapper.firstChild) {
      this.calendarWrapper.removeChild(this.calendarWrapper.firstChild);
    }

    data.forEach((month) => {
      if (month.occasions.length > 0) {
        const monthWrapper = this.createElement('div', ['month']);
        this.calendarWrapper.appendChild(monthWrapper);

        const monthObj = new Date(month.month);
        const monthHeading = this.createElement(
          'h3',
          null,
          monthObj.toLocaleDateString(this.currentLang, { month: 'long', year: 'numeric' }),
        );
        monthWrapper.appendChild(monthHeading);

        const monthOccasionsEl = this.createElement('div', ['occasions']);
        monthWrapper.appendChild(monthOccasionsEl);

        month.occasions.forEach((occasion) => {
          const {
            permalink,
            eventImage,
            eventTitle,
            eventExcerpt,
            startTime,
            endTime,
            interpretation,
            interpretationIconClass,
            label,
            labelIconClass,
            taxonomyTerms,
          } = occasion;

          const startTimeDt = DateTime.fromISO(startTime).setLocale(this.currentLang);
          const endTimeDt = DateTime.fromISO(endTime).setLocale(this.currentLang);

          const occasionWrapperEl = this.createElement('a', ['occasion', labelIconClass && labelIconClass], null, [{ href: permalink }]);
          monthOccasionsEl.appendChild(occasionWrapperEl);

          const ocassionImageWrapperEl = this.createElement('div', ['image-wrapper']);
          occasionWrapperEl.appendChild(ocassionImageWrapperEl);

          if (eventImage) {
            const ocassionImageEl = this.createElement('img', ['image'], null, [{ src: eventImage }]);
            ocassionImageEl.dataset.objectFit = 'cover';
            ocassionImageWrapperEl.appendChild(ocassionImageEl);
          }

          const infoWrapperEl = this.createElement('div', ['info-wrapper']);
          occasionWrapperEl.appendChild(infoWrapperEl);

          const ocassionDateEl = this.createElement('div', ['date'], `${startTimeDt.toLocaleString({ weekday: 'short' })} ${startTimeDt.toLocaleString({ day: 'numeric' })} ${startTimeDt.toLocaleString({ month: 'short' }).split('.').join('')}`);
          infoWrapperEl.appendChild(ocassionDateEl);

          // Create info
          const infoEl = this.createElement('div', ['info']);
          infoWrapperEl.appendChild(infoEl);

          const eventTypeLabelsEl = this.createElement('div', ['event-type-labels']);
          taxonomyTerms.forEach((term) => {
            const labelEl = this.createElement('span', ['event-type-label'], term.name);
            eventTypeLabelsEl.appendChild(labelEl);
          });
          infoEl.appendChild(eventTypeLabelsEl);

          const occasionTitleEl = this.createElement('span', ['title', '-large'], eventTitle);
          infoEl.appendChild(occasionTitleEl);

          const occasionExcerptEl = this.createElement('span', ['excerpt'], eventExcerpt);
          infoEl.appendChild(occasionExcerptEl);

          if (interpretation !== 'None') {
            const occasionInterpretation = this.createElement('span', ['interpretation', `-${interpretationIconClass}`], interpretation);
            infoEl.appendChild(occasionInterpretation);
          }

          const occasionTime = this.createElement('div', ['time']);
          infoEl.appendChild(occasionTime);

          if (label) {
            const labelEl = this.createElement('div', ['label', `${labelIconClass}`], label);
            infoEl.appendChild(labelEl);
          }

          const occasionTimeLabelEl = this.createElement(
            'span',
            ['time'],
            `${startTimeDt.toLocaleString({
              hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
            })} – ${endTimeDt.toLocaleString({
              hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
            })}`,

          );
          occasionTime.appendChild(occasionTimeLabelEl);
        });
      }
    });

    window.objectFitPolyfill();
  }

  toggleButton(buttonElement, showButton) {
    if (showButton) {
      buttonElement.classList.remove('-hidden');
    } else {
      buttonElement.classList.add('-hidden');
    }
  }

  createElement(tagName = 'div', classes, text, attributes) {
    const element = document.createElement(tagName);

    if (classes) {
      classes.forEach((cssClass) => {
        element.classList.add(cssClass);
      });
    }

    if (text) {
      const textNode = document.createTextNode(he.decode(text));
      element.appendChild(textNode);
    }

    if (attributes) {
      attributes.forEach((attribute) => {
        const attrKey = Object.keys(attribute)[0];
        const attrValue = Object.values(attribute)[0];
        element[attrKey] = attrValue;
      });
    }

    return element;
  }
}

// Register class
if (window.brix && window.brix.registerBlock) {
  window.brix.registerBlock('brix-event-calendar', BrixModuleEventCalendar.init);
}
